import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for TPA Management) ---
import {
    // Core Concepts & General
    faBalanceScale,
    faSmileBeam,
    faHandshake,        // TPA / Partnerships / Collaboration / Empanelment
    faHospitalUser,     // Patient Access / Patient Volume
    faClipboardCheck,   // Claims Processing / Compliance / Accuracy
    faDollarSign,       // Financial Health / Reimbursement / Revenue / Cost
    faTasks,            // Operational Efficiency / Streamlining
    faUsers,            // Patient Satisfaction / Feedback
    faCheckCircle,      // Quality / Success / Approval
    faFileInvoiceDollar,// Billing / Claims
    // Benefits
    faNetworkWired,     // Expanding Market Reach / Network Access
    faUsersCog,         // Reduced Operational Costs / Staff Focus
    // How Cliniqor Simplifies (Services)
    // faHandshake reused for Empanelment
    faAward,            // Accreditation / Compliance Support
    // faClipboardCheck reused for Claims Processing
    // faHandshake reused for Strategic Collaboration
    // faAward reused for Accreditation Facilitation
    // faUsers reused for Patient Feedback
    faChalkboardTeacher,// Training / Capacity Building
    faChartLine,        // Performance Monitoring / Improvement
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faProjectDiagram, faLightbulb, faChartLine, faMapSigns, faBullseye, faSitemap,
    faCalculator, faLaptopCode, faGavel, faHandHoldingDollar, faExpandArrowsAlt,
    faTrophy, faThumbsUp, faBuilding, faMedal, faSearchLocation, faBoxOpen, faComments,
    faTags, faStar, faPrint, faMousePointer, faNewspaper, faCalendarCheck, faCrosshairs,
    faUserTie, faStream, faReceipt, faSearchDollar, faSmileBeam, faRoute, faConciergeBell
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Vital?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Benefits for Growth
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // How Cliniqor Simplifies (Services)
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Maximizing Financial Benefits

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for insurance/management/finance
    const heroBgImage = 'https://images.unsplash.com/photo-1556740772-1a741367bd62?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing collaboration/paperwork

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>TPA Management Services</h1>
            <p className="cliniqor-sub-heading">
              Transforming Insurance Management for Healthcare Excellence by Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Effective TPA management is critical for financial health and seamless patient experiences. Cliniqor simplifies empanelment, claims, and reimbursement, ensuring operational sustainability and quality care.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY TPA MANAGEMENT IS VITAL --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why TPA Management is Vital for Hospitals and Healthcare Providers</h2>
              <p>Impacting financial viability and patient engagement.</p>
            </div>
             {/* Using Features Grid for the 5 vital points */}
            <div className="cliniqor-features-grid">
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                        <h4>Streamlined Claims Process</h4>
                        <p>Ensuring correct, efficient filing and tracking for timely processing.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHospitalUser} /></div>
                        <h4>Increased Patient Access</h4>
                        <p>Broadening the patient base through empanelment with leading insurers.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                        <h4>Faster Reimbursement</h4>
                        <p>Optimizing payment cycles by addressing claims promptly and minimizing bottlenecks.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div> {/* Legal/Compliance */}
                        <h4>Regulatory Compliance</h4>
                        <p>Adhering to insurance/government regulations, mitigating risks and penalties.</p>
                    </div>
                </div>
                 <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div> {/* Financial Health */}
                        <h4>Financial Health</h4>
                        <p>Maintaining strong cash flow through timely payments and efficient processing.</p>
                    </div>
                </div>
            </div>
          </div>
        </section>

        {/* --- BENEFITS FOR GROWTH (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>The Benefits of TPA Management for Hospital Growth</h2>
              <p>Strategic partnerships enabling your hospital to reach its full potential.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (5 items) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faNetworkWired} /></div>
                    <h4>Expanding Market Reach</h4>
                    <p>Exposure to larger patient base via private insurers, govt schemes (CGHS, ECHS, ESI).</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Footfall */}
                    <h4>Increased Footfall & Revenue</h4>
                    <p>Significant increase in patient inflow, higher occupancy rates, and revenue.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <h4>Reduced Operational Costs</h4>
                    <p>Lowering administrative burden, allowing staff focus on quality care.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                    <h4>Enhanced Operational Efficiency</h4>
                    <p>Automated claims, faster approvals, and transparent insurer communications.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSmileBeam} /></div> {/* Satisfaction */}
                    <h4>Enhanced Patient Satisfaction</h4>
                    <p>Seamless insurance handling leads to confident, returning, and recommending patients.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '20px' }}>
                 <p>Cliniqor makes TPA management an efficient, reliable, and profitable function within your hospital’s ecosystem.</p>
            </div>
          </div>
        </section>

        {/* --- HOW CLINIQOR SIMPLIFIES (Detailed Planning Section - 7 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>How Cliniqor Simplifies TPA Management</h2>
               <p>End-to-end services navigating complexities from empanelment to settlement.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 7 Service Areas */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandshake} /></div>
                    <div className="cliniqor-content"><h4>1. Empanelment Assistance</h4> <p>Guiding through documentation, evaluation, approval for major TPAs & govt schemes (CGHS, ECHS, ESI).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                    <div className="cliniqor-content"><h4>2. Insurance Claims Processing & Management</h4> <p>Ensuring accurate, timely submission, tracking lifecycle, resolving issues, optimizing reimbursement.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandshake} /></div> {/* Collaboration */}
                    <div className="cliniqor-content"><h4>3. Strategic Insurance Provider Collaboration</h4> <p>Building partnerships, negotiating favorable terms, expanding network access.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div>
                    <div className="cliniqor-content"><h4>4. Accreditation and Compliance Support</h4> <p>Facilitating accreditation to meet insurer requirements, assisting with audits.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsers} /></div> {/* Patient Feedback */}
                    <div className="cliniqor-content"><h4>5. Patient Feedback & Satisfaction Management</h4> <p>Collecting insights to refine TPA processes and enhance patient experience.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>6. Training & Capacity Building for Staff</h4> <p>Equipping admin/billing teams to handle claims, documentation, and negotiations effectively.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChartLine} /></div>
                    <div className="cliniqor-content"><h4>7. Performance Monitoring & Continuous Improvement</h4> <p>Using data insights to fine-tune strategies for claims efficiency and revenue cycles.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- MAXIMIZING FINANCIAL BENEFITS (Reusing Features Layout - 3 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Maximizing Financial Benefits Through TPA Management</h2>
                 <p>Unlocking significant growth potential for your hospital.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Financial Benefits --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Improved Cash Flow</h4>
                            <p>Streamlined claims and fast-track reimbursement ensure prompt payments.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHospitalUser} /></div>
                            <h4>Increased Patient Volume</h4>
                            <p>Empanelment with major insurers leads to higher patient inflow and stability.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                            <h4>Optimized Operational Efficiency</h4>
                            <p>Automating processes reduces overhead, enabling focus on quality care.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* No separate Conclusion section, integrated into Footer */}


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Partner with Cliniqor for Seamless TPA Management</h2>
          <p>Let Cliniqor help you navigate TPA complexities with ease and efficiency. From empanelment to claims and compliance, we provide the expertise, tools, and resources for your hospital to thrive. Elevating Your Hospital’s Insurance Partnerships for Sustainable Growth and Excellence.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss TPA Management</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;